import { useState } from 'react';
import { Icons } from '../ui/Icons';
import { CTAConfig, CTAType, CTAResponseData, UserProfile, Organization } from '../../types';
import { saveCTAResponse, saveCTAInteraction } from '../../services/firebase';
import { EmailSignupForm } from './forms/EmailSignupForm';
import { AccountPrompt } from './forms/AccountPrompt';
import { ChurchInviteCard } from './forms/ChurchInviteCard';
import { FreeBookForm } from './forms/FreeBookForm';
import { EventRsvpForm } from './forms/EventRsvpForm';

interface CTAInterstitialProps {
  cta: CTAConfig;
  studyId: string;
  segmentId: string;
  orgId: string;
  user: UserProfile | null;
  organization: Organization | null;
  onComplete: () => void;
  onSkip: () => void;
  onOpenAuthModal?: (mode: 'login' | 'signup') => void;
}

const getCTAIcon = (type: CTAType) => {
  switch (type) {
    case 'email_signup':
      return Icons.Mail;
    case 'account_creation':
      return Icons.UserPlus;
    case 'church_invite':
      return Icons.Church;
    case 'free_book':
      return Icons.Gift;
    case 'event_rsvp':
      return Icons.Calendar;
    default:
      return Icons.Megaphone;
  }
};

const getCTAColor = (type: CTAType): string => {
  switch (type) {
    case 'email_signup':
      return 'from-blue-600 to-blue-800';
    case 'account_creation':
      return 'from-green-600 to-green-800';
    case 'church_invite':
      return 'from-purple-600 to-purple-800';
    case 'free_book':
      return 'from-amber-600 to-amber-800';
    case 'event_rsvp':
      return 'from-rose-600 to-rose-800';
    default:
      return 'from-primary to-blue-800';
  }
};

export const CTAInterstitial = ({
  cta,
  studyId,
  segmentId,
  orgId,
  user,
  organization,
  onComplete,
  onSkip,
  onOpenAuthModal
}: CTAInterstitialProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const Icon = getCTAIcon(cta.type);
  const gradientClass = getCTAColor(cta.type);

  const handleSubmit = async (response: CTAResponseData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Save the response
      const responseId = await saveCTAResponse(
        orgId,
        studyId,
        segmentId,
        cta.type,
        user?.uid || null,
        response
      );

      // Track the interaction
      if (user?.uid) {
        await saveCTAInteraction(user.uid, studyId, segmentId, 'completed', responseId);
      }

      setIsCompleted(true);

      // Wait a moment to show success, then proceed
      setTimeout(() => {
        onComplete();
      }, 1500);
    } catch (err) {
      console.error('Error submitting CTA response:', err);
      setError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (user?.uid) {
      await saveCTAInteraction(user.uid, studyId, segmentId, 'skipped');
    }
    onSkip();
  };

  const renderForm = () => {
    if (isCompleted) {
      return (
        <div className="text-center py-8 animate-fade-in-up">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icons.Check className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Thank You!</h3>
          <p className="text-gray-600">
            {cta.config.type === 'email_signup' && (cta.config as any).successMessage
              ? (cta.config as any).successMessage
              : 'Your response has been submitted.'}
          </p>
        </div>
      );
    }

    switch (cta.type) {
      case 'email_signup':
        return (
          <EmailSignupForm
            config={cta.config as any}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        );
      case 'account_creation':
        return (
          <AccountPrompt
            config={cta.config as any}
            isAuthenticated={!!user}
            onSignup={() => onOpenAuthModal?.('signup')}
            onContinue={onComplete}
          />
        );
      case 'church_invite':
        return (
          <ChurchInviteCard
            config={cta.config as any}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        );
      case 'free_book':
        return (
          <FreeBookForm
            config={cta.config as any}
            user={user}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        );
      case 'event_rsvp':
        return (
          <EventRsvpForm
            config={cta.config as any}
            user={user}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return <p className="text-gray-600">Unknown CTA type</p>;
    }
  };

  return (
    <div className={`bg-gradient-to-br ${gradientClass} rounded-2xl text-white p-4 md:p-6`}>
      {/* Skip Button - Top right, prominent */}
      {!isCompleted && cta.type !== 'account_creation' && (
        <div className="flex justify-end mb-3">
          <button
            onClick={handleSkip}
            className="px-5 py-2.5 bg-white/25 hover:bg-white/40 text-white text-base font-semibold rounded-xl transition-colors flex items-center gap-2 shadow-lg"
          >
            Skip
            <Icons.Next className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* CTA Header */}
      <div className="text-center mb-4 animate-fade-in-up">
        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <h2 className="text-xl font-serif font-bold mb-1">{cta.title}</h2>
        {cta.description && (
          <p className="text-white/80 text-sm">{cta.description}</p>
        )}
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-xl p-4 shadow-xl">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-3 text-sm">
            {error}
          </div>
        )}
        {renderForm()}
      </div>
    </div>
  );
};
